import { AlertTriangle } from "lucide-react";

import { ShiftGuideBlock } from "@/features/concept-guides/components/ShiftGuideBlock";
import { shiftSubtitle, type ShiftRole } from "@/features/concept-guides/lib";
import { WarnStrip } from "@/features/orders/components/WarnTags";
import { useProductLabel } from "@/features/products/productLabelContext";
import { warnTags } from "@/features/orders/tags";
import { useAuthV2 as useAuth } from "@/lib/authv2/store";

import type { EkipShift } from "./api";

// Warn tags and shift guides on the 7-day schedule (decision 0128).

const FLAG = "Có ca mang thẻ cần lưu ý";

/**
 * Corner flag on a day of the 7-day grid that holds a shift with a warn tag.
 * Bottom corner, beside the short "ca": on phones the day's name wraps and a
 * top corner would cover it.
 */
export function DayWarnFlag({ orders }: { orders: readonly EkipShift[] }) {
  if (!orders.some((o) => warnTags(o.tags, o.isKol).length > 0)) return null;
  return (
    <span title={FLAG} className="absolute bottom-1.5 right-1.5 flex [color:var(--color-danger-text)]">
      <AlertTriangle size={13} aria-hidden />
      <span className="sr-only">{FLAG}</span>
    </span>
  );
}

/**
 * The guides a viewer reads for a shift: those of their seat when the shift
 * is theirs (a makeup artist booked as photographer reads the photo guides),
 * those of their department otherwise, none outside photo and makeup.
 */
function guideRole(o: EkipShift, department: string | undefined): ShiftRole | null {
  const dept = (department ?? "").toLowerCase();
  const own: ShiftRole | null = dept.startsWith("photo") ? "photo" : dept === "makeup" ? "makeup" : null;
  if (!own) return null;
  if (o.isMine) return o.isPhoto ? "photo" : "makeup";
  return own;
}

/** Top of a shift's detail: the "Lưu ý ca này" strip, then the shift's guides. */
export function ShiftWarnGuide({ order: o }: { order: EkipShift }) {
  const productLabel = useProductLabel();
  const me = useAuth((s) => s.currentStaff);
  const role = guideRole(o, me?.department);
  return (
    <>
      <WarnStrip labels={warnTags(o.tags, o.isKol)} className="mb-3.5" />
      {role ? (
        <ShiftGuideBlock
          target={{ selectedConcept: o.selectedConcept, productCode: o.product, category: o.category }}
          role={role}
          subtitle={shiftSubtitle({
            shootDate: o.shootDate,
            startTime: o.startTime,
            product: productLabel(o.product, { full: true }),
            role,
          })}
          className="mb-3 bg-surface"
        />
      ) : null}
    </>
  );
}
